// Audit questions for the AI Commercial Growth scorecard.
// Each option has a score 1-4 (1 = least mature, 4 = most mature).
// Written for construction, property and professional services firms.
// Q11, Q14, Q15 use dimension "Context" and are NOT included in the maturity score.
// They are passed to the LLM as prospect context so recommendations can be tailored.
export const DIMENSIONS = [
  "Strategy & Vision",
  "Data & Infrastructure",
  "AI & Automation",
  "Team & Skills",
  "Commercial Outcomes",
];

// Dimension name used for context-only questions (excluded from scoring).
export const CONTEXT_DIMENSION = "Context";

export const QUESTIONS = [
  {
    id: "q1",
    dimension: "Strategy & Vision",
    question:
      "How clearly is AI tied to winning more work and protecting margin in your firm?",
    options: [
      { label: "No formal connection yet", value: 1 },
      { label: "We're exploring, no plan in place", value: 2 },
      { label: "We have a written plan owned by leadership", value: 3 },
      { label: "Director-level priority, tied to revenue", value: 4 },
    ],
  },
  {
    id: "q2",
    dimension: "Strategy & Vision",
    question: "Who's actually driving the AI agenda in your firm right now?",
    options: [
      { label: "Nobody specific", value: 1 },
      { label: "IT, or a side project", value: 2 },
      { label: "A named person or function", value: 3 },
      { label: "A director with budget, mandate & KPIs", value: 4 },
    ],
  },
  {
    id: "q3",
    dimension: "Data & Infrastructure",
    question:
      "How would you describe the state of your client and pipeline data?",
    options: [
      { label: "Spread across spreadsheets, inboxes & people's heads", value: 1 },
      { label: "Mostly in a CRM, but messy", value: 2 },
      { label: "Clean, structured and mostly trusted", value: 3 },
      { label: "Unified, real-time, ready to plug AI into", value: 4 },
    ],
  },
  {
    id: "q4",
    dimension: "Data & Infrastructure",
    question:
      "Can your team safely connect AI to your internal systems (CRM, project data, finance)?",
    options: [
      { label: "No clear policy or pipes", value: 1 },
      { label: "Manual exports and copy-paste", value: 2 },
      { label: "Some integrations live", value: 3 },
      { label: "Governed, secure AI access layer", value: 4 },
    ],
  },
  {
    id: "q5",
    dimension: "AI & Automation",
    question: "What does your outbound actually look like today?",
    options: [
      { label: "Only referrals and word-of-mouth", value: 1 },
      { label: "We've tried outbound, it never stuck", value: 2 },
      { label: "It's running but inconsistent", value: 3 },
      { label: "A structured, automated outbound system is installed", value: 4 },
    ],
  },
  {
    id: "q6",
    dimension: "AI & Automation",
    question:
      "How many former or dormant clients are sitting in your database with no active follow-up?",
    options: [
      { label: "Hundreds, untouched", value: 1 },
      { label: "We have a list but no system to work it", value: 2 },
      { label: "We re-engage manually a few times a year", value: 3 },
      { label: "An automated reactivation sequence is running", value: 4 },
    ],
  },
  {
    id: "q7",
    dimension: "Team & Skills",
    question:
      "How fluent is your commercial / BD team with AI in their day-to-day?",
    options: [
      { label: "Curious but untrained", value: 1 },
      { label: "A few power users", value: 2 },
      { label: "Most of the team use AI weekly", value: 3 },
      { label: "AI is part of how we operate", value: 4 },
    ],
  },
  {
    id: "q8",
    dimension: "Team & Skills",
    question:
      "How dependent is business development on you (the founder / MD) personally?",
    options: [
      { label: "100%, if I stop, it stops", value: 1 },
      { label: "Heavy, I close everything, others support", value: 2 },
      { label: "Shared, I'm involved but not the bottleneck", value: 3 },
      { label: "I don't touch the day-to-day BD machine", value: 4 },
    ],
  },
  {
    id: "q9",
    dimension: "Commercial Outcomes",
    question:
      "Could you tell me right now where your revenue is coming from in the next 6 months?",
    options: [
      { label: "Honestly, no", value: 1 },
      { label: "Roughly, based on gut and recent wins", value: 2 },
      { label: "Yes, from a CRM I trust", value: 3 },
      { label: "Yes, by source, stage and conversion, in real time", value: 4 },
    ],
  },
  {
    id: "q10",
    dimension: "Commercial Outcomes",
    question: "When you tender or pitch for new work, do you know your win rate?",
    options: [
      { label: "No tracking at all", value: 1 },
      { label: "A rough sense, by gut", value: 2 },
      { label: "Tracked in spreadsheets", value: 3 },
      { label: "Live dashboard with reasons for win/loss", value: 4 },
    ],
  },
  {
    id: "q11",
    dimension: CONTEXT_DIMENSION,
    question: "Where do most of your new leads come from today?",
    options: [
      { label: "Referrals and word-of-mouth only", value: 1 },
      { label: "Referrals + some inbound (website / marketing)", value: 2 },
      { label: "Inbound + manual outbound", value: 3 },
      { label: "Inbound + structured outbound running", value: 4 },
    ],
  },
  {
    id: "q12",
    dimension: "Commercial Outcomes",
    question:
      "From first contact to signed work, how long is your typical sales cycle?",
    options: [
      { label: "12+ months", value: 1 },
      { label: "6 to 12 months", value: 2 },
      { label: "3 to 6 months", value: 3 },
      { label: "Under 3 months", value: 4 },
    ],
  },
  {
    id: "q13",
    dimension: "Data & Infrastructure",
    question: "What's running your sales pipeline today?",
    options: [
      { label: "Spreadsheets, email folders, memory", value: 1 },
      { label: "A basic CRM, not really used", value: 2 },
      { label: "HubSpot, Pipedrive, Salesforce or similar, actively used", value: 3 },
      { label: "CRM + connected tools, dashboards & automation", value: 4 },
    ],
  },
  {
    id: "q14",
    dimension: CONTEXT_DIMENSION,
    question:
      "What share of your annual revenue comes from repeat clients vs new clients?",
    options: [
      { label: "90%+ repeat clients", value: 1 },
      { label: "Around 70/30 repeat to new", value: 2 },
      { label: "Around 50/50 split", value: 3 },
      { label: "Mostly new logos every year", value: 4 },
    ],
  },
  {
    id: "q15",
    dimension: CONTEXT_DIMENSION,
    question: "What's the main reason you're looking at AI right now?",
    options: [
      { label: "Curiosity, not urgent", value: 1 },
      { label: "We're under-hitting revenue targets", value: 2 },
      { label: "Growing fast, can't scale BD", value: 3 },
      { label: "Competitors are pulling ahead with AI", value: 4 },
    ],
  },
];
